import React from "react";
import classNames from "classnames";
import frameStates from "./frameStates";
import { CircularLoader } from "../CircularLoader/CircularLoader";
import I18n from "pro-features/components/WithTranslation/src/I18n";

export const DefaultFrameLoader = (props) => {
    const { style = {}, ...other } = props;
    return (
        <div
            style={{ display: "flex", alignItems: "center", justifyContent: "center", ...style }}
            {...other}
        >
            <CircularLoader
                options={{
                    svg: {
                        stroke: "#dedede",
                        strokeWidth: "4",
                        r: 21,
                        cx: "50%",
                        cy: "50%",
                    },
                    style: {
                        width: 46,
                        height: 46,
                    },
                }}
            />
        </div>
    );
};

const DefaultFrameError = ({ errorMessageTop, errorMessageBottom, ...props }) => (
    <div
        className="u-flex-row u-justifyCenter u-flex-center"
        {...props}
    >
        <div className="noData-content">
            <div className="noData-icon">
                <i className="sw-icon-no-data" />
            </div>
            {errorMessageTop ? (
                <div className="u-uppercase">
                    <I18n>{errorMessageTop}</I18n>
                </div>
            ) : null}
            {errorMessageBottom ? (
                <div className="u-uppercase">
                    <I18n>{errorMessageBottom}</I18n>
                </div>
            ) : null}
        </div>
    </div>
);

type Content = React.ReactElement | boolean | React.ComponentType;

const renderContent = (content: Content, context: keyof typeof frameStates) => {
    if (React.isValidElement(content)) {
        // content is React element - just render it
        return content;
    } else {
        switch (typeof content) {
            case "boolean":
                const DefaultStateComponent = defaultProps[`on${context}`];
                return content && <DefaultStateComponent />; // the guard is to make sure we dont render anything
            // is case 'false' is passed
            case "function":
                const ContentComponent = content; // React component is passed in,
                return <ContentComponent />;

            default:
                return null; // unrecognized content type - dont render
        }
    }
};

const defaultErrorMessageTop = "global.nodata.notavilable",
    defaultErrorMessageBottom = ""; // no bottom message by default

const defaultProps = {
    onLoading: null,
    errorMessageTop: defaultErrorMessageTop,
    errorMessageBottom: defaultErrorMessageBottom,
    onError: (props) => (
        <DefaultFrameError
            errorMessageTop={defaultErrorMessageTop}
            errorMessageBottom={defaultErrorMessageBottom}
            {...props}
        />
    ),
    onNoData: (props) => (
        <DefaultFrameError
            errorMessageTop={defaultErrorMessageTop}
            errorMessageBottom={defaultErrorMessageBottom}
            {...props}
        />
    ),
};

interface SimpleFrameProps {
    className?: string;
    errorMessageBottom?: string; // if you want to use the defaultErrorComponent and just change the text;
    errorMessageTop?: string; // if you want to use the defaultErrorComponent and just change the text
    header?: React.ReactNode;
    onError?: Content;
    onLoaded?: Content;
    onLoading?: Content;
    onNoData?: Content;
    state: frameStates;
}

const SimpleFrame = ({
    state,
    header = null,
    onLoading = defaultProps.onLoading,
    onLoaded,
    onError = defaultProps.onError,
    onNoData = defaultProps.onNoData,
    errorMessageTop = defaultProps.errorMessageTop,
    errorMessageBottom = defaultProps.errorMessageBottom,
    ...otherProps
}: SimpleFrameProps) => {
    const { className, ...outsideProps } = otherProps;
    let content;
    switch (frameStates[frameStates[state]]) {
        case frameStates.Loading:
            content = onLoading;
            break;
        case frameStates.Loaded:
            content = onLoaded;
            break;
        case frameStates.Error:
            content = onError;
            break;
        case frameStates.NoData:
            content = onNoData;
            break;
    }
    return (
        <div
            data-frame-state={[frameStates[state]]}
            className={classNames("react-widget-frame", className)}
            {...outsideProps}
        >
            {header}
            {renderContent(content, frameStates[state] as keyof typeof frameStates)}
        </div>
    );
};

export default SimpleFrame;
